import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import { CircularProgress } from "@deere/fuel-react";
import EditTransactionModal from "./EditTransactionModal";
import {
  Check,
  Delete,
  Edit,
  ErrorOutline,
  HourglassEmpty,
} from "@mui/icons-material";
import { Button } from "@deere/fuel-react";
import ToastMessageFunction from "../../common/toast-message-function";
import {
  approveTransaction,
  deleteTransaction,
  getTransactionsForReviewAndApprove,
  validateTransaction,
} from "./utils/api";
import TransactionsTable from "../common/TransactionsTable";
import { useSelector } from "react-redux";
import { AMOUNT_THRESHOLDS } from "./utils/constants";

const ReviewAndApproveTransactions = () => {
  const toastManager = ToastMessageFunction();
  const user = useSelector((state) => state.userState.userData);
  const [isLoading, setIsLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [selectedDealer, setSelectedDealer] = useState("");

  const showToast = useCallback(
    (message, severity) => {
      toastManager.getToast(message, severity);
    },
    [toastManager]
  );

  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const response = await getTransactionsForReviewAndApprove(
        user,
        "ERROR"
      );
      if (response && response.success) {
        const mappedData = response.data.map((item) => {
          let memoRequest = null;
          let requestItems = [];
          let amount = 0;
          let currency = "";

          // Determine if the item is a debit or credit memo request
          if (item.debitMemoRequest) {
            memoRequest = { ...item.debitMemoRequest };
            requestItems = memoRequest.debitMemoRequestItem || [];
          } else if (item.creditMemoRequest) {
            memoRequest = { ...item.creditMemoRequest };
            requestItems = memoRequest.creditMemoRequestItem || [];
          }

          // Extract amount and currency from conditions
          if (requestItems.length > 0) {
            const conditions =
              requestItems[0][
                item.debitMemoRequest
                  ? "debitMemoRequestItemConditions"
                  : "creditMemoRequestItemConditions"
              ] || [];
            amount = conditions.length > 0 ? conditions[0]?.condValue || 0 : 0;
            currency =
              conditions.length > 0 ? conditions[0]?.currency || "" : "";
          }
           const totalAmount = item.totalAmount || (amount && requestItems.length > 0 ? amount * (requestItems[0]?.targetQty || 1) : 0);
           const threshold = AMOUNT_THRESHOLDS[currency];
           const isHighAmount = threshold !== undefined && Number(totalAmount) >= threshold;
           const itemTextString =
            requestItems.length > 0
              ? requestItems[0].itemText.join("\r\n")
              : "";

          return {
            ...memoRequest,
            billingDataId: item.billingDataId,
            source: item.billingDataId ? "UPLOAD" : "API",
            hasError: item.hasError,
            message: item.message || "Ready to Approve",
            dealerNumber: item.dealerNumber,
            dealerUnit: item.dealerUnit,
            dealerName: item.dealerName,
            amount,
            totalAmount: totalAmount,
            currencydisplay: currency,
            threshold ,
            isHighAmount,
            itemTextDisplay: itemTextString,
            transactionType: item.debitMemoRequest ? "Debit" : "Credit",
            duplicateMessage: item.duplicateMessage,
            isSapNumberUploaded:item.sapNumberUploaded,
            sapNumber:item.sapNumber,
            statusText:
              item.hasError === "true" ||
              (item.message && item.message.toLowerCase().includes("error"))
                ? "Error"
                : item.message &&
                    item.message.toLowerCase().includes("approved")
                  ? "Pending"
                  : item.duplicateMessage
                    ? "Duplicate":
                    isHighAmount? "High Amount"
                    : "Ready to Approve",
          };
        });

        // Sort by status: Errors first, then Pending/Approved, then Ready to Approve
        const sortedData = mappedData.sort((a, b) => {
          const aIsError =
            a.hasError === "true" ||
            (a.message && a.message.toLowerCase().includes("error"));
          const bIsError =
            b.hasError === "true" ||
            (b.message && b.message.toLowerCase().includes("error"));
          const aIsApproved =
            a.message && a.message.toLowerCase().includes("approved");
          const bIsApproved =
            b.message && b.message.toLowerCase().includes("approved");
          const aIsDuplicate = a.duplicateMessage;
          const bIsDuplicate = b.duplicateMessage;

          // Errors first
          if (aIsError && !bIsError) return -1;
          if (!aIsError && bIsError) return 1;

          // Then Pending/Approved
          if (!aIsError && aIsApproved && !bIsApproved) return -1;
          if (!aIsError && !aIsApproved && bIsApproved) return 1;

          // Then Duplicate
          if (!aIsError && !aIsApproved && aIsDuplicate && !bIsDuplicate)
            return -1;
          if (!aIsError && !aIsApproved && !aIsDuplicate && bIsDuplicate)
            return 1;
          // Then High Amount
const aIsHighAmount = a.isHighAmount;
const bIsHighAmount = b.isHighAmount;

if (!aIsError && !aIsApproved && !aIsDuplicate && aIsHighAmount && !bIsHighAmount) return -1;
if (!aIsError && !aIsApproved && !aIsDuplicate && !aIsHighAmount && bIsHighAmount) return 1;
          // Then Ready to Approve (not error, not approved)
          return 0;
        });

        setTransactions(sortedData);
        setFilteredTransactions(sortedData);
        setSelectedDealer("");
      } else {
        showToast(response.message || "Failed to fetch transactions", "error");
      }
    } catch (error) {
      showToast(error.message || "An error occurred", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    setFilteredTransactions(transactions);
  }, [transactions]);

  const handleEditClick = (transaction) => {
    setEditingTransaction(transaction);
    setModalOpen(true);
  };

  const editableFields = [
    "billingDataId",
    "serviceRenderDate",
    "material",
    "targetQty",
    "amount", // unit price
    "currencydisplay",
    "itemTextDisplay",
    "taxDepartureCountry",
    "dealerNumber",
    "profitCenter",
  ];

  const handleSaveClick = async (transactionDetails) => {
    const filterFields = (obj, fields) =>
      Object.fromEntries(
        Object.entries(obj).filter(([key]) => fields.includes(key)),
      );

    const validateTransactionDetails = {
      ...filterFields(transactionDetails, editableFields),
      itemText: transactionDetails.itemTextDisplay.replace(/\r\n/g, "\n"),
      condValue: transactionDetails.amount,
      currency: transactionDetails.currencydisplay,
      // dealerAccountNumber: transactionDetails.dealerNumber
    };

    delete validateTransactionDetails.itemTextDisplay;
    delete validateTransactionDetails.amount;
    delete validateTransactionDetails.currencydisplay;

    try {
      setModalOpen(false);
      setIsLoading(true);
      const response = await validateTransaction(validateTransactionDetails);

      if (response.success) {
        showToast(response.message, "success");
        fetchTransactions();
      } else {
        showToast(`${response.message}`, "error");
        fetchTransactions();
      }
    } catch (error) {
      console.error("Validation failed:", error.message);
      // Handle validation failure (e.g., show an error message)
      showToast(`${error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const updateItemText = (items, index, subIndex, value) => {
    const updatedItems = [...items];
    updatedItems[index].itemText[subIndex] = value;
    return updatedItems;
  };

  const updateItem = (items, index, key, value) => {
    const updatedItems = [...items];
    updatedItems[index][key] = value;
    return updatedItems;
  };

  // New handleInputChange function
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Split the name attribute to get key, index, and subKey if present
    const [key, index, subKey] = name.split("-");

    let newTransaction;

    if (subKey !== undefined) {
      // If subKey is defined, it means we are updating an itemText value
      newTransaction = {
        ...editingTransaction,
        debitMemoRequestItem: updateItemText(
          editingTransaction.debitMemoRequestItem,
          parseInt(index, 10), // Convert index to integer
          parseInt(subKey, 10), // Convert subIndex to integer
          value, // New value to be set
        ),
      };
    } else if (index !== undefined) {
      // If only index is defined, it means we are updating a property of debitMemoRequestItems
      newTransaction = {
        ...editingTransaction,
        debitMemoRequestItem: updateItem(
          editingTransaction.debitMemoRequestItem,
          parseInt(index, 10), // Convert index to integer
          key, // Key of the property to be updated
          value, // New value to be set
        ),
      };
    } else {
      // If neither index nor subKey is defined, we are updating a top-level property of the transaction
      newTransaction = { ...editingTransaction, [name]: value };
    }

    // Update the state with the new transaction
    setEditingTransaction(newTransaction);
  };

  const handleApproveClick = async (rows) => {
    const selectedRows = Array.isArray(rows) ? rows : [rows];
    const billingDataIds = [];
    const transactionIds = [];
    const messages = [];

    selectedRows.forEach((row) => {
      const { billingDataId, transactionId, message } =
        filteredTransactions[row.index];
      billingDataIds.push(parseInt(billingDataId, 10));
      transactionIds.push(transactionId === null ? "" : transactionId);
      messages.push(message);
    });

    const validBillingDataIds = billingDataIds.filter((id) =>
      Number.isInteger(id),
    );

    const hasProcessedTransactions = transactionIds.some((id) => id !== "");
    const hasApprovedMessages = messages.includes("APPROVED");
const hasErrorTransactions = selectedRows.some((row) => {
    const { hasError, message } = filteredTransactions[row.index];
    return (
      hasError === "true" ||
      (message && message.toLowerCase().includes("error"))
    );
  });
    if (hasProcessedTransactions || hasApprovedMessages) {
      showToast(
        <span>
          Unable to approve: Transactions have already been processed in SAP!!
        </span>,
        "warning",
      );
    }
     else if (hasErrorTransactions) {
    showToast(
      <span>
        Unable to approve: One or more selected transactions are in Error
        status.
      </span>,
      "warning",
    );
  } 
    else {
      try {
        setIsLoading(true);
        const response = await approveTransaction(validBillingDataIds);
        if (response.success) {
          showToast(response.message, "success");
          fetchTransactions();
        } else {
          showToast(`Approval failed: ${response.message}`, "error");
          fetchTransactions();
        }
      } catch (error) {
        showToast(`An error occurred: ${error.message}`, "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDeleteClick = async (rows) => {
    const selectedRows = Array.isArray(rows) ? rows : [rows];
    const billingDataIds = [];
    const transactionIds = [];
    const messages = [];
    const hasNoSAPDocuments = [];
    const statusTexts = [];

    selectedRows.forEach((row) => {
      const transaction = filteredTransactions[row.index];
      const { billingDataId, transactionId, message } = transaction;
      
      billingDataIds.push(parseInt(billingDataId, 10));
      transactionIds.push(transactionId === null ? "" : transactionId);
      messages.push(message);
      statusTexts.push(transaction?.statusText);
      //no request number, no invoice number, no account number , request status Error
      const noSAPDocument = 
        transaction?.requestNumber &&
        transaction?.invoiceNumber &&
        transaction?.accountNumber
        && transaction?.statusText !== "Pending"
        && transaction?.statusText === "Error";
        hasNoSAPDocuments.push(noSAPDocument);
    });

    const validBillingDataIds = billingDataIds.filter((id) =>
      Number.isInteger(id),
    );
    const hasProcessedTransactions = transactionIds.some((id) => id !== "");
    const hasApprovedMessages = messages.includes("APPROVED");
    const hasPendingRecords = statusTexts.some((status) => status === "Pending");
    
    // If all selected rows have no SAP documents, allow deletion; otherwise check if processed
    const allHaveNoSAPDocuments = hasNoSAPDocuments.every((noDoc) => noDoc === true);

    // Check for pending records
    if (hasPendingRecords) {
      showToast(
        <span>
          Unable to delete: Transactions are being processed in SAP!!
        </span>,
        "warning",
      );
    } else if (
      (hasProcessedTransactions || hasApprovedMessages) &&
      allHaveNoSAPDocuments
    ) {
      showToast(
        <span>
          Unable to delete: Transactions have already been processed in SAP!!
        </span>,
        "warning",
      );
    } else {
      try {
        setIsLoading(true);
        const response = await deleteTransaction(validBillingDataIds);
        if (response.success) {
          showToast(response.message, "success");
          fetchTransactions();
        } else {
          showToast(`Deletion failed: ${response.message}`, "error");
          fetchTransactions();
        }
      } catch (error) {
        showToast(`An error occurred: ${error.message}`, "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const columns = [
    {
      header: "Dealer Number",
      accessorKey: "dealerNumber",
      size: 100,
      Cell: ({ row }) => {
        const { dealerNumber, sapNumber, isSapNumberUploaded, source, dealerName } = row.original;
        const displayValue = isSapNumberUploaded && sapNumber && source === "UPLOAD" ? sapNumber : dealerNumber;
        return dealerName ? (
          <Tooltip title={dealerName}>
            <span style={{ cursor: "default" }}>{displayValue}</span>
          </Tooltip>
        ) : (
          <span>{displayValue}</span>
        );
      },
    },
    {
      header: "Dealer Country",
      accessorKey: "taxDestinationCountry",
      size: 100,
    },
    {
      header: "Total Amount",
      accessorKey: "totalAmount",
      size: 100,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        return value != null ? Number(value).toFixed(2) : "";
      },
    },
    {
      header: "Currency",
      accessorKey: "currencydisplay",
      size: 80,
    },
    {
      header: "Item Text",
      accessorKey: "itemTextDisplay",
      size: 80,
      Cell: ({ renderedCellValue }) => (
        <div style={{ whiteSpace: "pre-wrap", width: "525px" }}>
          {renderedCellValue}
        </div>
      ),
    },
    {
      header: "Status",
      accessorKey: "statusText",
      filterVariant: "select",
      filterSelectOptions: [
        "Error",
        "Pending",
        "Duplicate",
        "High Amount",
        "Ready to Approve",
      ],
      size: 150,
      Cell: ({ row }) => {
        const { hasError, message, duplicateMessage,isHighAmount,currencydisplay,threshold } = row.original;
        const isError =
          hasError === "true" ||
          (message && message.toLowerCase().includes("error"));
        const isApproved =
          message && message.toLowerCase().includes("approved");

        return isError ? (
          <Tooltip
            title={
              <div>
                {formatMessageAsArray(message).map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </div>
            }
          >
            <Chip label="Error" color="error" icon={<ErrorOutline />} />
          </Tooltip>
        ) : isApproved ? (
          <Chip
            label="Pending"
            icon={<HourglassEmpty />}
            sx={{
              bgcolor: "#FF9800",
              color: "#000",
              "& .MuiChip-icon": { color: "#000" },
            }}
          />
        ) : duplicateMessage ? (
          <Tooltip title={<div>{duplicateMessage}</div>}>
            <Chip
              label="Duplicate"
              sx={{
                bgcolor: "#FFEB3B",
                color: "#000",
                "& .MuiChip-icon": {
                  color: "#000",
                },
              }}
              icon={<Check />}
            />
          </Tooltip>
        ): isHighAmount ? (
    <Tooltip title={<div>Total amount equals or exceeds the {currencydisplay} threshold of {threshold}</div>}>
      <Chip
        label="High Amount"
        sx={{ bgcolor: "#FFEB3B", color: "#000", "& .MuiChip-icon": { color: "#000" } }}
        icon={<ErrorOutline />}
      />
    </Tooltip>
  ) : (
          <Chip label="Ready to Approve" color="success" icon={<Check />} />
        ) ;
      },
    },
    {
      header: "Source",
      size: 0,
      accessorKey: "source",
    },
    {
      header: "Type", // New column for transaction type
      accessorKey: "transactionType",
      size: 100,
    },
    {
      header: "Actions",
      size: 150,
      Cell: ({ row }) => {
        const { transactionId, message, hasError } = row.original;
        const isProcessed = transactionId !== null;
        const isApproved =
          message && message.toLowerCase().includes("approved");
        const isError =
          hasError === "true" ||
          (message && message.toLowerCase().includes("error"));

        // Case 1: No request number, no invoice number, no account number → no document created on SAP side
        const hasNoSAPDocument =
          !row.original?.requestNumber &&
          !row.original?.invoiceNumber &&
          !row.original?.accountNumber &&
          row.original?.billingDataId !== null; // Ensure billingDataId is null to consider it as no SAP document

        // Enable delete if no SAP document was created, otherwise disable if processed or pending
        const canDelete = (hasNoSAPDocument || !isProcessed) && row.original?.statusText !== "Pending";

        return (
          <Box>
            <Tooltip title="Edit Transaction">
              <Button
                variant="primary"
                onClick={() => handleEditClick(row.original)}
                style={{
                  marginRight: "8px",
                  backgroundColor:
                    row.original.source !== "UPLOAD" || isApproved
                      ? "#CCCCCC"
                      : undefined,
                }}
                disabled={row.original.source !== "UPLOAD" || isApproved}
              >
                <Edit />
              </Button>
            </Tooltip>
            <Tooltip title="Approve Transaction">
              <Button
                variant="contained"
                style={{ marginRight: "8px" }}
                color="success"
                onClick={() => handleApproveClick(row)}
                disabled={isError || isApproved}
              >
                <Check />
              </Button>
            </Tooltip>
            <Tooltip title="Delete Transaction">
              <Button
                variant="contained"
                color="error"
                onClick={() => handleDeleteClick(row)}
                disabled={!canDelete}
              >
                <Delete />
              </Button>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const formatMessageAsArray = (message) => {
    return message.split("\n");
  };

  const handleDealerChange = (event) => {
    const dealer = event.target.value;
    setSelectedDealer(dealer);
    if (dealer) {
      setFilteredTransactions(
        transactions.filter((transaction) => transaction.dealerUnit === dealer),
      );
    } else {
      setFilteredTransactions(transactions);
    }
  };

  return (
    <Box>
      {toastManager.toast}
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress
            aria-label={"Loading..."}
            data-fuel-size={"medium"}
          />
        </Box>
      ) : (
        <div>
          <Box
            sx={{
              padding: "20px",
              backgroundColor: "white",
              marginBottom: "20px",
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            <Typography
              variant="h3"
              sx={{ fontWeight: "bold", marginBottom: "15px", color: "black" }}
            >
              Filters
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", marginBottom: "10px", color: "black" }}
            >
              Dealer Unit
            </Typography>
            <FormControl sx={{ width: "30%" }} size="small">
              <Select
                value={selectedDealer}
                onChange={handleDealerChange}
                displayEmpty
              >
                <MenuItem value="">All</MenuItem>
                {Array.from(
                  new Set(
                    transactions.map((transaction) => transaction.dealerUnit),
                  ),
                ).map((dealer) => (
                  <MenuItem key={dealer} value={dealer}>
                    {dealer}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              sx={{
                marginLeft: "10px",
                color: "black",
                borderColor: "#CCCCCC",
                height: "40px",
                "&:hover": {
                  borderColor: "#CCCCCC",
                  backgroundColor: "#F0F0F0",
                },
              }}
              onClick={() => handleDealerChange({ target: { value: "" } })}
              size="big"
            >
              Clear
            </Button>
          </Box>

          {/* material table for review and approve transactions */}
          <TransactionsTable
            columns={columns}
            filteredTransactions={filteredTransactions}
            fetchTransactions={fetchTransactions}
            showToast={showToast}
            handleApproveClick={
              handleApproveClick
            } /* Approve Transaction Button will be visible if 'handleApproveClick' passed as props*/
            handleDeleteClick={
              handleDeleteClick
            } /* Delete Transaction Button will be visible if 'handleDeleteClick' passed as props*/
          />
        </div>
      )}
      <EditTransactionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editingTransaction={editingTransaction}
        handleInputChange={handleInputChange} // Ensure this is passed to the modal
        handleSaveClick={handleSaveClick}
        setEditingTransaction={setEditingTransaction}
      />
    </Box>
  );
};

export default ReviewAndApproveTransactions;
